import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment-timezone';
import queryString from 'query-string';

const SearchSelections = props => {

    const [selectedBillNumbers, setSelectedBillNumbers] = useState(props.selectedBillNumbers);
    const [selectedBillRangeBeginning, setSelectedBillRangeBeginning] = useState(props.selectedBillRangeBeginning);
    const [selectedBillRangeEnd, setSelectedBillRangeEnd] = useState(props.selectedBillRangeEnd);
    const [selectedChapterNumber, setSelectedChapterNumber] = useState(props.selectedChapterNumber);
    const [selectedKeywords, setSelectedKeywords] = useState(props.selectedKeywords);
    const [currentStatus, setCurrentStatus] = useState(props.currentStatus);
    const [selectedBillStatusCategory, setSelectedBillStatusCategory] = useState(props.selectedBillStatusCategory);
    const [selectedSession, setSelectedSession] = useState(props.selectedSession);
    const [billStatusStartDate, setBillStatusStartDate] = useState(props.billStatusStartDate);
    const [billStatusEndDate, setBillStatusEndDate] = useState(props.billStatusEndDate);
    const [selectedCommittee, setSelectedCommittee] = useState(props.selectedCommittee);
    const [selectedSubcommittee, setSelectedSubcommittee] = useState(props.selectedSubcommittee);
    const [selectedPatron, setSelectedPatron] = useState(props.selectedPatron);
    const [selectedPatronType, setSelectedPatronType] = useState(props.selectedPatronType);
    const [selectedChamber, setSelectedChamber] = useState(props.selectedChamber);
    const [selectedBillType, setSelectedBillType] = useState(props.selectedBillType);
    const [selectedSubject, setSelectedSubject] = useState(props.selectedSubject);
    const [includeFailed, setIncludeFailed] = useState(props.includeFailed);
    const scrollInterval = useRef(null);

    const [disabled, setDisabled] = useState(true);
    const [leftDisabled, setLeftDisabled] = useState(true);
    const [rightDisabled, setRightDisabled] = useState(true);

    // These do essentially the same thing, but are requred to be separate ...
    const [hasLoadedProps, toggleLoadedProps] = useState(false);
    const [hasLoadedOnce, toggleLoadedOnce] = useState(false);

    const prevProps = usePreviousValue(props);

    const selections = useRef(null);

    useEffect(() => {
        // On load, things that rely on fetches for data (patrons, subjects, and (sub)committees) aren't in props (and therefore state) when isFetching is toggled.
        // We need to wait till they are done to do so.
        // Best way I could think of was to check the query object v what is available to see if they match. Once they do, its been loaded.
        if (!hasLoadedProps) {
            const parsed = queryString.parse(window.location.search, { decode: false });
            if (parsed.q) {
                const base64 = parsed.q;
                const decodedSearch = window.atob(base64);
                const queryObj = JSON.parse(decodedSearch);
                if (queryObj &&
                    (queryObj.selectedPatron === null || queryObj.selectedPatron === '' || (queryObj.selectedPatron !== '' && props.selectedPatron?.value === queryObj.selectedPatron))
                    && (queryObj.selectedCommittee === null || queryObj.selectedCommittee === '' || (props.selectedCommittee?.value === queryObj.selectedCommittee))
                    && (queryObj.selectedSubcommittee === null || queryObj.selectedSubcommittee === '' || (queryObj.selectedSubcommittee !== '' && props.selectedSubcommittee?.value === queryObj.selectedSubcommittee))
                    && (queryObj.selectedSubject === null || queryObj.selectedSubject === '' || (queryObj.selectedSubject !== '' && props.selectedSubject?.value === queryObj.selectedSubject))
                    && ((!Array.isArray(queryObj.selectedPatronType) || queryObj.selectedPatronType.length === 0) && (!Array.isArray(props.selectedPatronType || props.selectedPatronType.length === 0)) ||
                        (JSON.stringify(props.selectedPatronType.toSorted((a, b) => a.value > b.value ? 1 : -1).map(x => x.value)) === JSON.stringify(props.selectedPatronType.toSorted((a, b) => a.value > b.value ? 1 : -1).map(x => x.value))))) {
                    toggleLoadedProps(true);
                    toggleLoadedOnce(true);
                }
            }
            updateSearchCriteria();
        } else if ((prevProps?.subcommitteeList ?? []).length !== (props?.subcommitteeList ?? []).length) {
            if (props.selectedSubcommittee) {
                updateSearchCriteria();
                updateButtons();
            }
        } else if (hasLoadedProps && (prevProps.isFetching !== props.isFetching)) {
            if (props.searchExecuted) {
                let element = document.getElementById("your-search-anchor");
                if (element && window.innerWidth <= 500) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'start'
                    });
                    props.handleToggleSearchExecuted && props.handleToggleSearchExecuted(false);
                }
            }
            updateSearchCriteria();
            setDisabled(props.isFetching);
            updateButtons();
        }
    }, [props, hasLoadedProps]);

    useEffect(() => {
        setDisabled(false);
        updateButtons();
    }, [hasLoadedOnce]);

    const updateButtons = () => {
        if (selections.current) {
            setRightDisabled(true);
            setLeftDisabled(true);
            if (selections.current.scrollLeft > 0) {
                setLeftDisabled(false);
            }
            if (selections.current?.scrollLeft + selections.current?.clientWidth + 1 < selections.current?.scrollWidth) {
                setRightDisabled(false);
            }
        }
    }

    const updateSearchCriteria = () => {
        setSelectedBillNumbers(props.selectedBillNumbers);
        setSelectedBillRangeBeginning(props.selectedBillRangeBeginning);
        setSelectedBillRangeEnd(props.selectedBillRangeEnd);
        setSelectedChapterNumber(props.selectedChapterNumber);
        setSelectedKeywords(props.selectedKeywords);
        setCurrentStatus(props.currentStatus);
        setSelectedBillStatusCategory(props.selectedBillStatusCategory);
        setSelectedSession(props.selectedSession);
        setBillStatusStartDate(props.billStatusStartDate);
        setBillStatusEndDate(props.billStatusEndDate);
        setSelectedCommittee(props.selectedCommittee);
        setSelectedSubcommittee(props.selectedSubcommittee);
        setSelectedPatron(props.selectedPatron);
        setSelectedPatronType(props.selectedPatronType);
        setSelectedChamber(props.selectedChamber);
        setSelectedBillType(props.selectedBillType);
        setSelectedSubject(props.selectedSubject);
        setIncludeFailed(props.includeFailed);
    }

    const handleScroll = (e) => {
        if (e.target.scrollLeft === 0) {
            setLeftDisabled(true);
            clearInterval(scrollInterval.current);
        } else if (e.target.scrollLeft + e.target.clientWidth + 1 >= e.target.scrollWidth) {
            setRightDisabled(true);
            clearInterval(scrollInterval.current);
        } else {
            setLeftDisabled(false);
            setRightDisabled(false);
        }
    }

    const interceptChange = (fcn) => {
        toggleLoadedOnce(false);
        toggleLoadedProps(false);
        setDisabled(true);
        fcn();
    }

    const clearFilters = () => {
        const a = document.createElement('a');
        const params = {
            selectedSession: selectedSession.SessionID,
            currentStatus: false,
            includeFailed: false,
            allLegislation: true,
            uniqueSearch: Math.random(),
        };
        const str = window.btoa(JSON.stringify(params));
        if (window.location.href.indexOf('bill-management') > -1) {
            a.href = '/bill-management?q=' + str;
        } else {
            a.href = '/bill-search?q=' + str;
        }
        a.click();
    }

    const startScrolling = (increment) => {
        scrollInterval.current = setInterval(() => {
            if (selections.current) {
                selections.current.scrollLeft += increment ? 10 : -10;
            }
        }, 25);
    }

    const stopScrolling = () => {
        clearInterval(scrollInterval.current);
    }

    const isEssentiallyAllLegislationOrMostFrequentlyAccessed = !props.introductionDate && !selectedBillNumbers && !selectedBillRangeBeginning && !selectedBillRangeEnd && !selectedChapterNumber
        && !selectedKeywords && !selectedBillStatusCategory && !billStatusStartDate && !billStatusEndDate
        && !selectedCommittee && !selectedSubcommittee && !selectedPatron && selectedPatronType.length === 0 && !selectedChamber
        && !selectedBillType && !selectedSubject && (!includeFailed || (includeFailed && (props.allLegislation || props.mostFrequent)));

    return (
        <div className={"search-selections " + (isEssentiallyAllLegislationOrMostFrequentlyAccessed ? ' all-most-frequent-legislation' : '')}>
            <button className={'arrow-chevron-left'} type='button' disabled={leftDisabled || disabled} onTouchStart={e => startScrolling(false)} onTouchEnd={stopScrolling} onMouseDown={e => startScrolling(false)} onMouseUp={stopScrolling}>&lt;</button>
            <ul ref={selections} className='selections' onScroll={e => handleScroll(e)}>
                <Selection
                    value={selectedBillNumbers}
                    name={"selectedBillNumbers"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedBillRangeBeginning}
                    name={"selectedBillRangeBeginning"}
                    title="Bill Range Start"
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedBillRangeEnd}
                    name={"selectedBillRangeEnd"}
                    title="Bill Range End"
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedChapterNumber}
                    name={"selectedChapterNumber"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedKeywords}
                    name={"selectedKeywords"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={currentStatus && selectedBillStatusCategory ? selectedSession && !selectedSession.IsActive ? 'Final Status' : 'Current Status' : null}
                    name={"currentStatus"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedBillStatusCategory ? selectedBillStatusCategory.label : null}
                    name={"selectedBillStatusCategory"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={billStatusStartDate ? moment(billStatusStartDate).tz('America/New_York').format("M/D/YYYY") : null}
                    name={"billStatusStartDate"}
                    title="Status Start Date"
                    clear={null}
                    disabled={true}
                />
                <Selection
                    value={billStatusEndDate ? moment(billStatusEndDate).tz('America/New_York').format("M/D/YYYY") : null}
                    name={"billStatusEndDate"}
                    title="Status End Date"
                    clear={null}
                    disabled={true}
                />
                <Selection
                    value={selectedCommittee ? selectedCommittee.label : null}
                    name={"selectedCommittee"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedSubcommittee ? selectedSubcommittee.label : null}
                    name={"selectedSubcommittee"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedPatron ? selectedPatron.label : null}
                    name={"selectedPatron"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                {selectedPatronType.map((type, typeIndex) =>
                    <Selection
                        key={typeIndex}
                        index={typeIndex}
                        value={type.label}
                        name={"selectedPatronType"}
                        clear={() => interceptChange(() => props.handleMultiSelectionClear("selectedPatronType", typeIndex, true))}
                        disabled={disabled}
                    />
                )}
                <Selection
                    value={selectedChamber ? selectedChamber.label : null}
                    name={"selectedChamber"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedBillType ? selectedBillType.label : null}
                    name={"selectedBillType"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={selectedSubject ? selectedSubject.label : null}
                    name={"selectedSubject"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={includeFailed ? "Include Failed" : null}
                    name={"includeFailed"}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
                <Selection
                    value={props.introductionDate ? moment(props.introductionDate).tz('America/New_York').format("M/D/YYYY") : null}
                    name={'introductionDate'}
                    clear={e => interceptChange(() => props.handleSelectionClear(e))}
                    disabled={disabled}
                />
            </ul>
            <button disabled={rightDisabled || disabled} type='button' className='arrow-chevron-right' onTouchStart={e => startScrolling(true)} onTouchEnd={stopScrolling} onMouseDown={e => startScrolling(true)} onMouseUp={stopScrolling}>&gt;</button>
            <button className='txt-dark-blue clear' onClick={clearFilters}>Clear Filters</button>
            <hr className="faded-line" />
        </div>
    )
}

const Selection = props => {
    const { title, value, name, index, clear, disabled } = props;
    if (value) {
        return (<li className={"selection-bubble " + (disabled ? 'disabled' : '')}>
            <div className="flex-row" onClick={() => !disabled && clear(name, index)}>
                <span>{title ? `${title}: ${value.length > 100 ? value.substring(0, 100) + '...' : value}` : value.length > 100 ? value.substring(0, 100) + '...' : value}</span>
                {clear && <button aria-label="remove item" onClick={(e) => { e.preventDefault() }} className={"icon " + (disabled ? 'cross-grey' : 'cross-blue')}></button>}
            </div>
        </li>);
    } else {
        return (false);
    }
}

const usePreviousValue = value => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

export default SearchSelections;
